import { MenuOutlined } from "@ant-design/icons"
import { Button, Flex, Segmented, Typography } from "antd"
import React, { useCallback } from "react"
import { useTranslation } from "react-i18next"

import { SearchBar } from "../../../../components/Input/SearchBar.tsx"
import { useAppSelector } from "../../../../hooks/use-app-selector.ts"
import { type PermissionProjectPermissions } from "../../../SettingsPermissions/types/permissions.types.ts"
import { ProjectsLeadApprovalTrigger } from "../../ProjectsLeadApproval/ProjectsLeadApprovalTrigger.tsx"
import { useProjectsViewSwitch } from "../../helpers/projects-view-preference.ts"
import type { ProjectFilters } from "../../redux/projects.types.ts"

const { Title, Text } = Typography

interface ProjectListGridHeaderProps {
  projectPermissions: PermissionProjectPermissions
  search: string | null
  onFilersChange: (data: Partial<ProjectFilters>) => void
  onRefetchProjectsCallback: () => void
  onImport: (file: File) => Promise<void>
  onDownloadTemplate: () => Promise<void>
}

export function ProjectListGridHeader({
  projectPermissions,
  search,
  onFilersChange,
  onRefetchProjectsCallback,
  onImport,
  onDownloadTemplate
}: ProjectListGridHeaderProps) {
  const { t } = useTranslation()
  const switchProjectsView = useProjectsViewSwitch()
  // The list shows its Table / Grid switch behind the redesign flag, so the
  // grid mirrors it: the same control, whichever of the two is open.
  const redesign = useAppSelector(
    (state) => !!state.auth.userInfo?.feature_projects_redesign
  )

  const onSearchHandler = useCallback(
    (q: string | null) => onFilersChange({ q }),
    [onFilersChange]
  )

  return (
    <Flex justify="space-between" wrap align="center" className={"mb-4"}>
      <div>
        <Title level={2} className="m-0 text-start">
          Projects
        </Title>
        <Text type="secondary">Visual overview of all company projects.</Text>
      </div>
      <Flex gap={8} wrap>
        <SearchBar
          placeholder="Search projects..."
          defaultValue={search || ""}
          onSearch={onSearchHandler}
        />

        {projectPermissions?.projects?.project_create && (
          <ProjectsLeadApprovalTrigger
            onRefetchCallback={onRefetchProjectsCallback}
            onImport={onImport}
            onDownloadTemplate={onDownloadTemplate}
          />
        )}

        {redesign ? (
          <Segmented
            aria-label={t("projectsCommand.viewSwitch.label")}
            value="grid"
            onChange={(value) => {
              if (value === "table") switchProjectsView("list")
            }}
            options={[
              { value: "table", label: t("projectsCommand.viewSwitch.table") },
              { value: "grid", label: t("projectsCommand.viewSwitch.grid") }
            ]}
          />
        ) : (
          <Button
            type={"primary"}
            onClick={() => switchProjectsView("list")}
            icon={<MenuOutlined />}
          >
            View List
          </Button>
        )}
      </Flex>
    </Flex>
  )
}
